import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, pipe, throwError } from 'rxjs';
import { map, catchError, share } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { NbDialogService } from '@nebular/theme';
import { User } from '../models/user';
import { PromptDialogComponent } from '../theme/components/prompt-dialog/prompt-dialog.component';
import { EnvService } from './env.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public currentUserSubject: BehaviorSubject<User>;
  public currentUser: Observable<User>;

  public userWithPinSubject: BehaviorSubject<any>;
  public userWithPinData: Observable<any>;

  public hasPassedLandingSubject: BehaviorSubject<any>;
  public hasPassLanding: Observable<any>;

  public tahunSkpSubject: BehaviorSubject<any>;
  public tahunSkp: Observable<any>;

  constructor(
    private router: Router,
    private http: HttpClient,
    private dialogService: NbDialogService,
    private envService: EnvService,
  ) {
    this.currentUserSubject = new BehaviorSubject<User>(
      JSON.parse(localStorage.getItem('currentUser')),
    );
    this.currentUser = this.currentUserSubject.asObservable();

    this.userWithPinSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('userWithPin')),
    );
    this.userWithPinData = this.userWithPinSubject.asObservable();

    this.hasPassedLandingSubject = new BehaviorSubject<any>(
      localStorage.getItem('hasPassedLanding'),
    );
    this.hasPassLanding = this.hasPassedLandingSubject.asObservable();
    this.tahunSkpSubject = new BehaviorSubject<any>(
      JSON.parse(localStorage.getItem('tahun_skp')),
    );
    this.tahunSkp = this.tahunSkpSubject.asObservable();
  }

  public get currentUserValue(): User {
    return this.currentUserSubject.value;
  }

  public get userWithPinValue() {
    return this.userWithPinSubject.value;
  }

  public get hasPassedLandingValue() {
    return this.hasPassedLandingSubject.value;
  }

  public get tahunSkpValue() {
    return this.tahunSkpSubject.value;
  }

  /**
   * this is used to clear anything that needs to be removed
   */
  clear() {
    localStorage.removeItem('currentUser');
    this.currentUserSubject.next(null);
    localStorage.removeItem('userWithPin');
    this.userWithPinSubject.next(null);
    localStorage.removeItem('tahun_skp');
    this.tahunSkpSubject.next(null);
    localStorage.removeItem('MENU_DATA_CACHE');
    localStorage.removeItem('MENU_DATA_TIME');
    localStorage.removeItem('PRESENCE_LOCATIONS_CACHE');
    localStorage.removeItem('PRESENCE_LOCATIONS_TIME');
  }

  /**
   * check for expiration and if token is still existing or not
   * @return {boolean}
   */
  isAuthenticated() {
    if (this.currentUserValue) {
      return true;
    } else {
      return false;
    }
  }

  isPinAuthenticated() {
    if (this.userWithPinValue) {
      return true;
    } else {
      return false;
    }
  }

  authenticatePin(pin) {
    return this.http
      .post(this.envService.env.apiUrl + 'monitoring/loginwith-pin', pin)
      .pipe(
        map((res) => {
          if (res && res['success']) {
            let user = res['data'];
            localStorage.setItem('userWithPin', JSON.stringify(user));
            this.userWithPinSubject.next(user);
          }
          return res;
        }),
      );
  }

  isTokenExpired(): boolean {
    return false;
  }

  loginAdmin(): void {
    localStorage.setItem(
      'token',
      `eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJpc3MiOiJPbmxpbmUgSldUIEJ1aWxkZXIiLCJpYXQiOjE1MzMyNzM5NjksImV4cCI6MTU2NDgxMDAwNSwiYXVkIjoid3d3LmV4YW1wbGUuY29tIiwic3ViIjoiVGVzdCBHdWFyZCIsIkdpdmVuTmFtZSI6IkpvaG5ueSIsIlN1cm5hbWUiOiJSb2NrZXQiLCJFbWFpbCI6Impyb2NrZXRAZXhhbXBsZS5jb20iLCJyb2xlIjoiQWRtaW4ifQ.rEkg53_IeCLzGHlmaHTEO8KF5BNfl6NEJ8w-VEq2PkE`,
    );

    this.router.navigate(['/app/dashboard']);
  }

  login(data, ckey) {
    return this.http
      .post<any>(this.envService.env.apiUrl + 'login?ckey=' + ckey, data, {
        withCredentials: true,
      })
      .pipe(
        map((res) => {
          if (res && res.success) {
            this.storeUserData(res);
          }
          return res;
        }),
      );
  }

  checkNip(nip) {
    const body = {
      nip: nip,
    };
    return this.http
      .post<any>(this.envService.env.apiUrl + 'aktivasi', body)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error) => {
          return throwError('Something went wrong!');
        }),
      );
  }

  checkToken() {
    const token = this.currentUserValue.api_token;
    const userId = this.currentUserValue.id;
    const params = { user_id: userId.toString(), token: token };
    return this.http
      .get<any>(this.envService.env.apiUrl + 'check-token', { params })
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error) => {
          return throwError('Something went wrong!');
        }),
      );
  }

  register(body, ckey) {
    return this.http
      .post<any>(this.envService.env.apiUrl + 'registrasi?ckey=' + ckey, body)
      .pipe(
        map((data: any) => {
          return data;
        }),
        catchError((error) => {
          return throwError('Something went wrong!');
        }),
      );
  }

  verifyAccount(id, token, expires, signature) {
    let url =
      this.envService.env.apiUrl +
      'email/verify/' +
      id +
      '/' +
      token +
      '?expires=' +
      expires +
      '&signature=' +
      signature;
    return this.http.get<any>(url).pipe();
  }

  resetPassword(body, ckey) {
    return this.http
      .post<any>(
        this.envService.env.apiUrl + 'reset-password?ckey=' + ckey,
        body,
      )
      .pipe(
        map((data: any) => {
          return data;
        }),
      );
  }

  resetPendaftaran(nip) {
    return this.http
      .get<any>(this.envService.env.apiUrl + 'reset-daftar?nip=' + nip)
      .pipe(
        map((res: any) => {
          return res;
        }),
      );
  }

  confirmResetPendaftaran(body, ckey) {
    return this.http
      .post<any>(this.envService.env.apiUrl + 'reset-daftar?ckey=' + ckey, body)
      .pipe(
        map((res: any) => {
          return res;
        }),
      );
  }

  logoutPrompt() {
    return this.dialogService
      .open(PromptDialogComponent, {
        autoFocus: false,
        context: {
          header: 'Confirmation',
          body: 'Are you sure to logout ?',
          status: 'warning',
        },
      })
      .onClose.pipe();
  }

  logout(): Observable<any> {
    let request = this.http
      .post<any>(this.envService.env.apiUrl + 'auth/logout', {})
      .pipe(share());
    return request;
  }

  logoutPin(): Observable<any> {
    let request = this.http
      .post<any>(this.envService.env.apiUrl + 'monitoring/logout', {})
      .pipe(share());
    return request;
  }

  profil(): void {
    this.router.navigate(['/app/profil']);
  }

  getTahunSkp() {
    return this.http
      .get(this.envService.env.apiUrl + 'data/tahun-skp-aktif')
      .pipe(
        map((data) => {
          localStorage.setItem('tahun_skp', JSON.stringify(data['data']));
          this.tahunSkpSubject.next(data['data']);
          return data;
        }),
      );
  }

  async storeUserData(data) {
    try {
      var user = data.data.user;
      user.pegawai = data.data.pegawai || null;
      if (data.data.sanctum_token) {
        user.api_token = data.data.sanctum_token;
      }

      await localStorage.setItem('currentUser', JSON.stringify(user));
      this.currentUserSubject.next(user);

      return data;
    } catch (error) {
      console.error('Error saat menyimpan data user:', error);
      throw error;
    }
  }

  passedLanding() {
    localStorage.setItem('hasPassedLanding', 'passed');
    this.hasPassedLandingSubject.next('passed');
    return true;
  }

  getUser() {
    return this.http.get(this.envService.env.apiUrl + 'user').pipe(
      map((res) => {
        return res;
      }),
    );
  }
}
